import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { COLORS, SHADOWS, FONTS } from '../styles/tokens';
import { APP_ROLE_PREFIX } from '../constants/auth-methods';
import { RequestCard } from '../components/RequestCard';
import {
  fetchPendingRequests,
  fetchRealmRoles,
  approveUser,
  denyUser,
  type AccountRequest,
  type KeycloakRole,
} from '../api/keycloak-admin';

/** Admin page for managing account access requests */
export function AccountRequests(): React.JSX.Element {
  const { username, token } = useAuth();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  const [requests, setRequests] = useState<AccountRequest[]>([]);
  const [appRoles, setAppRoles] = useState<KeycloakRole[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<Record<string, Set<string>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [pending, roles] = await Promise.all([
        fetchPendingRequests(token, username),
        fetchRealmRoles(token),
      ]);
      setRequests(pending);
      setAppRoles(roles.filter(r => r.name.startsWith(APP_ROLE_PREFIX)));

      const defaults: Record<string, Set<string>> = {};
      for (const req of pending) {
        defaults[req.user.id] = new Set(roles.filter(r => r.name.startsWith(APP_ROLE_PREFIX)).map(r => r.name));
      }
      setSelectedRoles(defaults);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [token, username]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /** Toggle a role selection for a user */
  function toggleRole(userId: string, roleName: string): void {
    setSelectedRoles(prev => {
      const userRoles = new Set(prev[userId] ?? []);
      if (userRoles.has(roleName)) {
        userRoles.delete(roleName);
      } else {
        userRoles.add(roleName);
      }
      return { ...prev, [userId]: userRoles };
    });
  }

  /** Handle approve action */
  async function handleApprove(userId: string): Promise<void> {
    setActionInProgress(userId);
    try {
      const selected = selectedRoles[userId] ?? new Set();
      const rolesToAssign = appRoles.filter(r => selected.has(r.name));
      await approveUser(token, userId, rolesToAssign);
      await loadData();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Failed to approve: ${msg}`);
    } finally {
      setActionInProgress(null);
    }
  }

  /** Handle deny action */
  async function handleDeny(userId: string): Promise<void> {
    setActionInProgress(userId);
    try {
      await denyUser(token, userId);
      await loadData();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`Failed to deny: ${msg}`);
    } finally {
      setActionInProgress(null);
    }
  }

  return (
    <div className="vx-page-extra-pad" style={{ padding: '32px' }}>
      {/* Page header */}
      <h2
        style={{
          fontFamily: FONTS.heading,
          fontSize: '20px',
          fontWeight: 400,
          letterSpacing: '0.2em',
          color: colors.text,
          textTransform: 'uppercase',
          margin: '0 0 8px',
        }}
      >
        Account Requests
      </h2>
      <p
        style={{
          fontFamily: FONTS.body,
          fontSize: '14px',
          color: colors.textSecondary,
          margin: '0 0 24px',
        }}
      >
        Manage pending access requests from new users
      </p>

      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: isDark ? '#3b1c1c' : '#fde8e8',
            border: '1px solid #EF4444',
            borderRadius: '8px',
            color: '#EF4444',
            fontFamily: FONTS.body,
            fontSize: '13px',
            marginBottom: '16px',
          }}
        >
          {error}
          <button
            onClick={loadData}
            style={{
              marginLeft: '12px',
              background: 'none',
              border: 'none',
              color: '#EF4444',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontFamily: FONTS.body,
              fontSize: '13px',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <p style={{ fontFamily: FONTS.body, color: colors.textSecondary }}>
          Loading requests...
        </p>
      )}

      {/* Empty state */}
      {!loading && !error && requests.length === 0 && (
        <div
          style={{
            backgroundColor: colors.card,
            boxShadow: isDark ? SHADOWS.cardDark : SHADOWS.cardLight,
            borderRadius: '12px',
            padding: '48px 32px',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontFamily: FONTS.heading,
              fontSize: '16px',
              color: colors.textSecondary,
              letterSpacing: '0.1em',
              margin: 0,
            }}
          >
            No pending requests
          </p>
          <p
            style={{
              fontFamily: FONTS.body,
              fontSize: '13px',
              color: colors.textSecondary,
              margin: '8px 0 0',
            }}
          >
            New users who register will appear here for approval
          </p>
        </div>
      )}

      {/* Request cards */}
      {!loading && requests.map(req => (
        <RequestCard
          key={req.user.id}
          request={req}
          appRoles={appRoles}
          selectedRoles={selectedRoles[req.user.id] ?? new Set()}
          onToggleRole={toggleRole}
          isDropdownOpen={openDropdown === req.user.id}
          onDropdownOpenChange={(open) => setOpenDropdown(open ? req.user.id : null)}
          actionInProgress={actionInProgress === req.user.id}
          onApprove={handleApprove}
          onDeny={handleDeny}
          isDark={isDark}
          colors={colors}
        />
      ))}

      {/* Refresh button */}
      {!loading && (
        <div style={{ marginTop: '16px' }}>
          <button
            onClick={loadData}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: `1px solid ${colors.border}`,
              backgroundColor: 'transparent',
              color: colors.textSecondary,
              fontFamily: FONTS.heading,
              fontSize: '11px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      )}
    </div>
  );
}
